import { memo, useMemo } from "react";
import styled from "styled-components";

import {
  isSeriesDomain,
  resolveSeriesDomain,
  type NormalizedSeries,
  type SeriesLane,
  type SeriesResolvedLane,
} from "./seriesData";
import { createTimelineSegmentIndex } from "../MediaTimelineBar/timelineSegmentIndex";
import { SeriesCanvas } from "./SeriesCanvas";
import { SeriesReadout } from "./SeriesReadout";
import type { SeriesReadoutLabels } from "./seriesSampling";
import type {
  MediaTimelineSegment,
  MediaTimelineSeriesDomain,
} from "../MediaTimelineBar/timelineTypes";

// Passive paint like the band layer: same z step, later in DOM order so lines sit on the bands
// and stay under the viewport navigation, range handles and the playhead. Pointer-transparent
// on purpose — a telemetry line spans the whole row and must not swallow click-to-seek.
const SeriesLayer = styled.div`
  position: absolute;
  z-index: 1;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;
const LaneBox = styled.div`
  position: absolute;
  inset-inline: 0;
`;
/* Lanes are one chart read as bands, so the seam is the quietest divider step, not a row border. */
const LaneSeam = styled.div`
  position: absolute;
  inset-inline: 0;
  height: 0;
  border-top: 1px solid ${({ theme }) => theme.color.border.divider};
`;

export interface SeriesPlotProps {
  gutter?: number;
  hoverIntent?: "line" | "plot";
  hoverDelayMs?: number;
  unit?: string;
  onTimeSelect?: (timeSeconds: number) => void;
  selectionHint?: string;
  /** Already normalized (`normalizeSeries`); a single unlabeled lane. */
  series?: readonly NormalizedSeries[];
  /** Stacked lanes; a lane without its own `domain` shares the row axis. Takes precedence over `series`. */
  lanes?: readonly SeriesLane[];
  /** Authored row axis; omitted or invalid means the full data extent of the lanes sharing it. */
  domain?: MediaTimelineSeriesDomain;
  viewportStart: number;
  viewportDuration: number;
  /** Picks the clock form of the popup time; the timeline passes its media duration. */
  latestTimeSeconds?: number;
  /** Already normalized bands painted beneath this row; ink lines borrow their hue over them. */
  bands?: readonly MediaTimelineSegment[];
  label?: string;
  /** Caption under the values popup title, e.g. the source key of the row's series. */
  description?: string;
  suppressReadout?: boolean;
  readoutLabels?: SeriesReadoutLabels;
  /**
   * Line kept at full strength while the others recede, across lanes: a lane without it recedes
   * whole. The readout is unaffected.
   */
  emphasizedSeriesId?: string | null;
  /**
   * Caption the authored shared axis in the values popup. For a plot without drawn ticks (a
   * timeline row); a chart with an axis leaves it off. Lanes on their own axis keep their own
   * captions, and the shared range is left out once no lane shares it.
   */
  showAxisRange?: boolean;
}

/** Memoized paint and an independent shared-time readout; the canvas has no per-series DOM. */
const NO_SERIES: readonly NormalizedSeries[] = [];
/** Stand-in axis for a lane with nothing to scale; nothing is drawn or read against it. */
const EMPTY_LANE_DOMAIN: MediaTimelineSeriesDomain = { min: 0, max: 1 };

export const SeriesPlot = memo(function SeriesPlot({
  series = NO_SERIES,
  lanes,
  gutter,
  hoverIntent,
  hoverDelayMs,
  unit,
  onTimeSelect,
  selectionHint,
  domain,
  viewportStart,
  viewportDuration,
  latestTimeSeconds,
  bands,
  label,
  description,
  suppressReadout = false,
  readoutLabels,
  emphasizedSeriesId,
  showAxisRange = false,
}: SeriesPlotProps) {
  // tracks 배열이나 같은 값의 domain 객체가 바뀌어도 축 계산과 Canvas 참조는 유지한다.
  const validDomain = isSeriesDomain(domain);
  const min = validDomain ? domain.min : undefined;
  const max = validDomain ? domain.max : undefined;
  const resolvedLanes = useMemo<readonly SeriesLane[]>(
    () => lanes ?? [{ id: "", series }],
    [lanes, series],
  );
  const allSeries = useMemo(
    () =>
      resolvedLanes.length === 1
        ? resolvedLanes[0]!.series
        : resolvedLanes.flatMap((lane) => lane.series),
    [resolvedLanes],
  );
  // The shared axis spans only the lanes that share it: a lane on its own axis neither widens
  // nor narrows the others.
  const sharedSeries = useMemo(
    () =>
      resolvedLanes.every((lane) => !isSeriesDomain(lane.domain))
        ? allSeries
        : resolvedLanes
            .filter((lane) => !isSeriesDomain(lane.domain))
            .flatMap((lane) => lane.series),
    [resolvedLanes, allSeries],
  );
  const sharedDomain = useMemo(
    () => resolveSeriesDomain({ min, max }, sharedSeries),
    [min, max, sharedSeries],
  );
  // A lane whose axis cannot be resolved (a shared-axis lane with nothing to plot) keeps its band
  // and caption empty instead of taking the resolved lanes down with it; only when no lane has an
  // axis is there nothing to mount.
  const settledLanes = useMemo<readonly SeriesResolvedLane[] | null>(() => {
    let resolved = false;
    const lanes = resolvedLanes.map((lane) => {
      const own = isSeriesDomain(lane.domain);
      const domain = own ? lane.domain : sharedDomain;
      if (domain) resolved = true;
      return { ...lane, domain: domain ?? EMPTY_LANE_DOMAIN, ownDomain: own };
    });
    return resolved ? lanes : null;
  }, [resolvedLanes, sharedDomain]);
  const index = useMemo(() => (bands ? createTimelineSegmentIndex(bands) : undefined), [bands]);
  const visibleBands = useMemo(
    () => index?.query(viewportStart, viewportStart + viewportDuration),
    [index, viewportStart, viewportDuration],
  );
  if (allSeries.length === 0 || !settledLanes) return null;
  const laneCount = settledLanes.length;
  const emphasisLane =
    emphasizedSeriesId == null
      ? -1
      : settledLanes.findIndex((lane) =>
          lane.series.some((entry) => entry.id === emphasizedSeriesId),
        );
  const axisRange =
    showAxisRange && validDomain && settledLanes.some((lane) => !lane.ownDomain)
      ? sharedDomain
      : null;
  return (
    <>
      <SeriesLayer aria-hidden="true" data-media-timeline-series="" data-lane-count={laneCount}>
        {settledLanes.map((lane, index) => (
          <LaneBox
            key={lane.id || index}
            data-media-timeline-lane={lane.id || undefined}
            data-media-timeline-lane-axis={lane.ownDomain ? "own" : undefined}
            style={{ top: `${(index * 100) / laneCount}%`, height: `${100 / laneCount}%` }}
          >
            <SeriesCanvas
              gutter={gutter}
              series={lane.series}
              domain={lane.domain}
              viewportStart={viewportStart}
              viewportDuration={viewportDuration}
              bands={visibleBands}
              emphasizedSeriesId={emphasizedSeriesId}
              emphasisElsewhere={emphasisLane >= 0 && emphasisLane !== index}
            />
          </LaneBox>
        ))}
        {settledLanes.slice(1).map((lane, index) => (
          <LaneSeam
            key={`seam:${lane.id || index}`}
            data-media-timeline-lane-seam=""
            style={{ top: `${((index + 1) * 100) / laneCount}%` }}
          />
        ))}
      </SeriesLayer>
      <SeriesReadout
        hoverIntent={hoverIntent}
        hoverDelayMs={hoverDelayMs}
        unit={unit}
        onTimeSelect={onTimeSelect}
        selectionHint={selectionHint}
        gutter={gutter}
        lanes={settledLanes}
        bands={visibleBands}
        viewportStart={viewportStart}
        viewportDuration={viewportDuration}
        latestTimeSeconds={latestTimeSeconds}
        label={label}
        description={description}
        axisRange={axisRange}
        suppressed={suppressReadout}
        labels={readoutLabels}
      />
    </>
  );
});
